// Common types
export interface ValidationResult<T> {
  isSuccess: boolean;
  entity: T;
  errors?: string[];
  message?: string;
}

export interface PagedResponse<T> {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  items: T[];
}

// Order related types
export interface OrderItem {
  id: number;
  orderId: number;
  serviceId: number;
  serviceName: string;
  serviceType: string;
  actualPrice: number;
  partPrice?: number;
  marginalPrice?: number;
  serviceCost: number;
  quantity: number;
  itemTotal: number;
  serviceStatus: string;
  notes: string;
  warrantyPeriod: number;
  warrantyEndDate: Date | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: number;
  warrantyTypeId: number | null;
  warrantyName: string;
  warrantyTypeDescription: string;
  warrantyCoveredItems: string;
  warrantyNotCoveredItems: string;
  serviceImage: string;
}

export interface Order {
  id: number;
  orderNumber: string;
  customerName: string;
  customerMobile: string;
  customerEmail: string;
  customerAddress: string;
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  orderDate: Date;
  expectedDeliveryDate: Date;
  actualDeliveryDate?: Date;
  lastUpdated: Date;
  orderStatus: string;
  OrderType?: string;
  totalItemPrice: number;
  totalServiceCost: number;
  discountAmount: number;
  gstPercentage: number;
  gstAmount: number;
  finalAmount: number;
  paymentStatus: string;
  paymentMethod: string;
  amountPaid: number;
  balanceAmount: number;
  technicianId: number;
  technicianName: string;
  notes: string;
  deviceCondition: string;
  warrantyPeriod: number;
  warrantyEndDate: Date | null;
  createdAt: Date;
  createdBy: number;
  updatedAt: Date;
  updatedBy: number;
  isActive: boolean;
  isGstInvoice?: boolean;
  customerGstin?: string;
  customerCompanyName?: string;
  orderItems: OrderItem[];
  orderStatusHistory: OrderStatusHistory[];
  orderPayments: OrderPayment[];
}

export interface OrderSchedule {
  id?: number;
  orderId: number;
  scheduledDate: string;
  scheduledTime?: string;
  technicianId?: number;
  technicianName?: string;
  notes?: string;
  createdAt?: string;
}

export interface OrderReplacement {
  id?: number;
  orderId: number;
  replacementCost: number;
  technicianId?: number;
  technicianName?: string;
  replacementDate: string;
  replacementStatus: string;
  notes?: string;
  messageSent?: boolean;
  createdAt?: string;
}

export interface OrderStatusHistory {
  id: number;
  orderId: number;
  status: string;
  notes: string;
  createdAt: Date;
  createdBy: number;
  order: string;
}

export interface OrderPayment {
  id: number;
  orderId: number;
  amount: number;
  paymentMethod: string;
  paymentStatus: string;
  transactionId: string;
  paymentDate: Date;
  notes: string;
  createdAt: Date;
  createdBy: number;
}

// Technician types
export interface Technician {
  id: number;
  name: string;
  mobile: string;
  email: string;
  status: string; // Active, Inactive, On Leave
  specialization: string;
  experience: number;
  rating: number;
  totalOrders: number;
  completedOrders: number;
  averageCompletionTime: number;
  imagePath: string;
  createdAt: Date;
  updatedAt: Date;
  darkStoreId?: number | null;
  darkStoreName?: string | null;
}

export interface TechnicianPerformance {
  technicianId: number;
  technicianName: string;
  totalOrders: number;
  completedOrders: number;
  averageRating: number;
  averageCompletionTime: number;
  currentWorkload: number;
  ordersInProgress: number;
  completionRate: number;
  monthlyStats: {
    month: string;
    ordersCompleted: number;
    averageRating: number;
    averageCompletionTime: number;
  }[];
}

// Admin user / role management
export interface AdminUser {
  id: number;
  name: string;
  mobile: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  roles: string[];
  technicianIds: number[];
}

export interface AdminRole {
  id: number;
  name: string;
  description?: string;
  createdAt: string;
}

export interface AdminUserRequest {
  name: string;
  mobile: string;
  email?: string;
  isActive: boolean;
  roles: string[];
  technicianIds: number[];
}

// Lead types
export interface Lead {
  id: number;
  name: string;
  mobile: string;
  email?: string;
  address?: string;
  languagePreferred?: string;
  source: string;
  status: string;
  priority: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  assignedTo?: number | null;
  assignedToName?: string;
  lastContactDate?: string | null;
  nextFollowUpDate?: string | null;
  expectedValue: number;
  actualValue: number;
  deviceType?: string;
  deviceModel?: string;
  serviceRequired?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  cart?: CartItem[];
  totalItemPrice?: number;
  totalServiceCost?: number;
  discountAmount?: number;
  finalAmount?: number;
}

export interface LeadFollowUp {
  id: number;
  leadId: number;
  followUpDate: Date;
  followUpType: string;
  notes: string;
  outcome: string;
  nextFollowUpDate?: Date;
  createdAt: Date;
  createdBy: number;
  createdByName: string;
}

export interface LeadCallLog {
  id: number;
  leadId: number;
  customerMobile: string;
  telecallerUserId?: number | null;
  telecallerName?: string;
  callStatus: string;
  leadStatusAfterCall?: string;
  callStartedAt: string;
  callAnsweredAt?: string | null;
  callEndedAt?: string | null;
  durationSeconds?: number | null;
  disposition?: string;
  notes?: string;
  recordingUrl?: string;
  recordingFileName?: string;
  recordingUploaded: boolean;
  isSuspicious: boolean;
  suspiciousReason?: string;
  createdAt: string;
}

// Product types
export interface Product {
  id: number;
  modelId: number;
  makeId: number;
  make: string;
  modelName: string;
  serviceName: string;
  status: number;
  actualPrice: number;
  markedPrice: string;
  serviceDuration: number;
  serviceCost: number;
  marginCost?: number;
  serviceImage: string;
  modelImage: string;
  createdAt: string;
  updatedAt: string;
  serviceType: string;
  warrantyPeriod: number;
  warrantyTypeId: number | null;
  warrantyName: string;
  warrantyTypeDescription: string;
  warrantyCoveredItems: string;
  warrantyNotCoveredItems: string;
  warrantyEndDate: Date | null;
}

// Service Types
export interface ServiceType {
  id: number;
  serviceName: string;
  actualPrice: number;
  marginalCost: number;
  serviceCost: number;
  serviceType: string;
  warrantyPeriod: number;
  modelId: number;
  serviceImage: string;
  serviceDuration: number;
  markedPrice: string;
  makeId: number;
  makeName: string;
  modelName: string;
  warrantyCoveredItems: string;
  warrantyNotCoveredItems: string;
  warrantyTypeDescription: string;
  warrantyName: string;
  warrantyTypeId: number | null;
  warrantyEndDate: Date | null;
}

// Cart Item extends ServiceType with quantity
export interface CartItem extends ServiceType {
  quantity: number;
}

// Make and Model types
export interface Make {
  id: number;
  name: string;
  status: number;
  imagePath: string;
}

export interface Model {
  id: number;
  name: string;
  makeId: number;
  imagePath: string;
  status: number;
}

// User types
export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Customer types
export interface Customer {
  id: number;
  name: string;
  mobile: string;
  email?: string;
  address?: string;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Dashboard types
export interface DashboardStats {
  totalOrdersCount: number;
  totalRevenue: number;
  totalServiceCost: number;
  totalPartsPrice?: number;
  onlinePaymentReceived: number;
  cashPaymentReceived: number;
  totalExpenses?: number;
  totalProfit?: number;
  profitMarginPercentage?: number;
}

// API Request types
export interface OrderQueryRequest {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  technicianId?: number;
  makeId?: number;
  modelId?: number;
}

export interface OrderStatusUpdateRequest {
  status: string;
  notes: string;
  userId: number;
}

// Contest types
export interface Contest {
  id: number;
  title: string;
  description?: string;
  /** 'order_count' | 'revenue' */
  contestType: string;
  targetValue: number;
  /** 'cash' | 'bonus' | 'gift' | 'voucher' */
  rewardType: string;
  rewardValue: number;
  rewardDescription?: string;
  startDate: string;
  endDate: string;
  bannerColor: string;
  isActive: boolean;
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
}

export interface ContestWithProgress extends Contest {
  currentValue: number;
  isCompleted: boolean;
  completedAt?: string;
  rewardPaid: boolean;
  progressPercentage: number;
}

export interface ContestLeaderboardEntry {
  rank: number;
  technicianId: number;
  technicianName: string;
  currentValue: number;
  isCompleted: boolean;
}

// Expense types
export interface Expense {
  id: number;
  expenseType: string;
  title: string;
  amount: number;
  expenseDate: string;
  description?: string;
  fileUrl?: string;
  fileName?: string;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
  isActive: boolean;
}

export interface ExpenseQueryRequest {
  page?: number;
  pageSize?: number;
  expenseType?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export const EXPENSE_TYPES = [
  'Rent',
  'Salary',
  'Parts / Inventory',
  'Utilities',
  'Marketing',
  'Transportation',
  'Equipment',
  'Miscellaneous',
] as const;

export interface LeadSummary {
  totalLeadsCount: number;
  newLeadsCount: number;
  inProgressLeadsCount: number;
  convertedLeadsCount: number;
  closedLeadsCount: number;
  totalExpectedValue: number;
  totalActualValue: number;
  totalFinalAmount: number;
  leadsWithFollowUpToday: number;
  leadsWithFollowUpTomorrow: number;
  highPriorityLeadsCount: number;
  mediumPriorityLeadsCount: number;
  lowPriorityLeadsCount: number;
}

// ── Inventory / Stock Management ──────────────────────────────────────────────

export interface DarkStore {
  id: number;
  name: string;
  address?: string;
  city?: string;
  contactPerson?: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryItem {
  id: number;
  darkStoreId: number;
  darkStoreName: string;
  serviceId: number;
  serviceName: string;
  makeName: string;
  modelName: string;
  quantity: number;
  lowStockThreshold: number;
  isLowStock: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryTransaction {
  id: number;
  darkStoreId: number;
  darkStoreName: string;
  serviceId: number;
  serviceName: string;
  transactionType: 'RESTOCK' | 'DEDUCTION' | 'ADJUSTMENT' | 'TRANSFER_OUT' | 'TRANSFER_IN';
  quantity: number;
  quantityBefore: number;
  quantityAfter: number;
  referenceType?: string;
  referenceId?: number;
  notes?: string;
  createdBy?: number;
  createdAt: string;
}

export interface InventorySummaryItem {
  serviceType: string;
  totalSkus: number;
  inStock: number;
  lowStock: number;
  outOfStock: number;
  totalUnits: number;
}

export interface RestockRequest {
  darkStoreId: number;
  serviceId: number;
  quantity: number;
  notes?: string;
  createdBy: number;
}

export interface AdjustStockRequest {
  darkStoreId: number;
  serviceId: number;
  newQuantity: number;
  notes?: string;
  createdBy: number;
}

export interface TransferStockRequest {
  fromDarkStoreId: number;
  toDarkStoreId: number;
  serviceId: number;
  quantity: number;
  notes?: string;
  createdBy: number;
}

// Technician Stock types
export interface TechnicianStockItem {
  id: number;
  technicianId: number;
  technicianName: string;
  technicianMobile: string;
  serviceId: number;
  serviceName: string;
  makeName: string;
  modelName: string;
  orderId?: number | null;
  orderNumber?: string | null;
  darkStoreId?: number | null;
  darkStoreName?: string | null;
  quantity: number;
  createdAt: string;
  updatedAt: string;
}

export interface TechnicianStockTransaction {
  id: number;
  technicianId: number;
  technicianName: string;
  serviceId: number;
  serviceName: string;
  makeName: string;
  modelName: string;
  orderId?: number | null;
  orderNumber?: string | null;
  darkStoreId?: number | null;
  darkStoreName?: string | null;
  transactionType: 'ISSUED' | 'RETURNED' | 'CONSUMED';
  quantity: number;
  quantityBefore: number;
  quantityAfter: number;
  notes?: string | null;
  createdBy?: number | null;
  createdAt: string;
}

export interface IssueStockRequest {
  technicianId: number;
  serviceId: number;
  quantity: number;
  orderId?: number | null;
  darkStoreId?: number | null;
  notes?: string;
  createdBy?: number | null;
}

export interface ReturnStockRequest {
  technicianId: number;
  serviceId: number;
  quantity: number;
  orderId?: number | null;
  restockDarkStoreId?: number | null;
  notes?: string;
  createdBy?: number | null;
}

